// UndoBar — R1's undo affordance (W39.1 / ADR-0080).
//
// **Not a snackbar.** ADR-0080 supersedes ADR-0078's "floating bar docked
// above the bottom nav": that position is Material's, and `DESIGN.md §16` bans
// Material patterns in this app — which is a plain iOS 26 design language. It
// is also the wrong shape here, because heshe's bottom edge is the W38.1
// three-island nav with a 60px rest / 52px collapsed pair, and a bar docked
// above it would need a separate offset per state while 52 e2e assertions hold
// that geometry. The bar appears where the delete happened, so the user's eyes
// never leave the thing they just tapped.
//
// Four things the first screenshot pass proved, all of them corrections:
//
//   1. **Zero reflow.** The bar is `position: fixed` and never enters the
//      document flow, so showing it cannot move a pixel of the content around
//      it. A pushed-open bar jumps the card the user is looking at.
//   2. **Opaque, not glass.** At `glass-thick` (96% alpha) over body copy, the
//      diary text was legible straight through 「日記已刪除」. Glass belongs to
//      the nav islands, which overlay chrome where nothing legible competes.
//   3. **Clear of the pill.** Docked 108px up: the islands are 60px at rest
//      plus `bottom-4` plus the safe-area inset, and at 96px the pill sat on
//      top of the message. An undo bar that cannot be read is the one failure
//      this pattern cannot have.
//   4. **The countdown is not an animation.** Under
//      `prefers-reduced-motion: reduce` the ring stops rotating but the 8s
//      keeps running — reduced motion is about motion, not about the user
//      losing sight of how long their undo window is open.
//
// The ring is a pure-CSS `steps()` sweep keyed to the same 8s `useUndoBar`
// counts down, so the markup carries no inline `style` at all — state rides on
// `data-*` and class (DESIGN §8 / §16).

import { RotateCcw } from "lucide-react";

import { cn } from "@/lib/utils/cn";

export interface UndoBarProps {
  open: boolean;
  /** What was deleted, in the user's words. */
  message?: string;
  onUndo?: () => void;
  /** Fired when the bar is dismissed by tap (not by the countdown). */
  onDismiss?: () => void;
  className?: string;
  /** `inline` sits in the flow's shadow; `floating` pins to a container. */
  placement?: "inline" | "floating";
}

export function UndoBar({
  open,
  message = "已刪除",
  onUndo,
  onDismiss,
  className,
  placement = "inline",
}: UndoBarProps) {
  if (!open) return null;

  return (
    <div
      data-component="UndoBar"
      data-open="true"
      data-placement={placement}
      // Marks that the bar is showing, not a countdown readout — the comment
      // here used to claim "the progress lives in a data attribute", which was
      // never true: this attribute is the constant string "running" and the
      // hook had a 10Hz `progress` that **no component read**. The ring is a
      // pure-CSS sweep, so there is no progress to publish. The attribute stays
      // because it distinguishes the open bar from a closed one in queries,
      // and it reads as a state rather than a measurement.
      data-countdown="running"
      role="status"
      aria-live="polite"
      className={cn(
        // **Opaque, not glass.** The first screenshot pass caught this: at
        // `glass-thick` (96% alpha) the bar sat over body copy and the two sets
        // of text read through each other — 「R1 screenshot diary」 was legible
        // straight through 「日記已刪除」. A bar that overlays content has to
        // win outright, so this is `--paper-elevated`, the same surface the
        // cards use. Glass is for the nav islands, which overlay *chrome* where
        // nothing legible is competing.
        "rounded-2xl border border-hairline-strong bg-paper-elevated",
        "shadow-modal",
        // **`bottom-0` is what makes the caller's `bottom-[108px]` mean
        // anything** (W50.1 §2). Without a vertical anchor the bar fell to its
        // *static position*, which in a zero-height slot wrapper is the slot's
        // own top edge — so the slot's offset anchored an empty shell and the
        // bar rendered 56px **below** it, straight through the pill.
        //
        // Measured at 390x844 (W50.1 §2.1): bar occupied y 736-792 while the
        // pill occupied 774-834 — 18px of vertical x 358px of horizontal
        // overlap = **6,444 px^2** of message sitting under the navigation.
        // `bottom-0` puts the bar at y 680-736, i.e. **38px of clearance**.
        //
        // This is exactly the §2.4 lesson from W50.0, one round later: the
        // probe measured `[data-testid="*undo-slot"]`, a container whose only
        // child was absolutely positioned, so it reported `h=0` and every
        // comparison against it returned a clean, plausible, wrong answer.
        "absolute inset-x-0 bottom-0 z-10",
        "flex items-center gap-s-3 px-4 h-14",
        // Enter is opacity + a 4px rise. `motion-reduce` collapses it to
        // opacity only, per the reduced-motion contract.
        "motion-safe:animate-[undoBarIn_var(--dur-quick)_ease-paper]",
        className,
      )}
    >
      {/*
        Countdown ring.

        The sweep is a **pure-CSS `steps()` animation** keyed to the same 8s the
        hook counts down, not a value passed from JS. That is what keeps three
        hard constraints true at once:
          - no inline `style` (DESIGN §8 / §16) — nothing to drift into an
            animated transform
          - `prefers-reduced-motion: reduce` stops the ring while the **8s keeps
            running** in `useUndoBar`. The countdown is time semantics, not
            decoration: the user must be able to see how long their undo window
            is open. `motion-reduce:animate-none` freezes it at full; the bar
            still disappears on time.
          - **nothing re-renders while the bar is open**, because nothing counts
            down in JS. The `8s` below is the ring's own duration and has to
            stay equal to `UNDO_DURATION_MS` — two literals for one number is
            exactly how a ring and its window drift apart silently, so
            `UndoBar.test.tsx` pins the two together.
      */}
      <span
        aria-hidden
        data-testid="undo-bar-ring"
        className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-pill border-[1.5px] border-hairline-strong"
      >
        <span
          data-ring="true"
          className={cn(
            "absolute inset-0 rounded-pill overflow-hidden",
            "motion-safe:animate-[undoBarCountdown_8s_linear_forwards]",
            "motion-reduce:animate-none",
          )}
        />
      </span>

      <p data-testid="undo-bar-message" className="flex-1 text-body-sm text-ink-1">
        {message}
      </p>

      {/* W49.0 P2-4. `onUndo` is optional: `restore_wish` is author-only while
          `delete_wish` is open to either partner, so for someone else's wish the
          only thing the bar could honestly offer is a button that 403s. A dead
          control is worse than no control — so the bar states the fact and the
          affordance goes away with it. */}
      {onUndo ? (
        <button
          type="button"
          data-testid="undo-bar-action"
          onClick={onUndo}
          onDoubleClick={onDismiss}
          // 「復原」 is the only interactive element in the bar. It is a real
          // <button>, so Enter / Space activation and the 44pt floor come from
          // the element rather than from a hand-written key handler (DESIGN §15).
          className={cn(
            "press tap-target min-h-11 min-w-11 shrink-0",
            "inline-flex items-center gap-1.5 rounded-pill px-3",
            "text-body-sm font-semibold text-you-strong",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
          )}
        >
          <RotateCcw size={14} strokeWidth={1.5} aria-hidden />
          復原
        </button>
      ) : null}
    </div>
  );
}
